import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import { TRACK_EVENT_TYPE, UI_EVENT_TYPE } from '@atlassian/analytics-web-react';
import { Box } from '@atlaskit/primitives/box';
import { Inline } from '@atlaskit/primitives/inline';
import { Stack } from '@atlaskit/primitives/stack';
import { Text } from '@atlaskit/primitives/text';
import { xcss } from '@atlaskit/primitives/xcss/xcss';
import Button from '@atlaskit/button/button';
import IconButton from '@atlaskit/button/icon/button';
import AddIcon from '@atlaskit/icon/core/add';
import SelectClearIcon from '@atlaskit/icon/core/cross-circle';
import CrossIcon from '@atlaskit/icon/core/cross';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import SearchIcon from '@atlaskit/icon/core/search';
import Textfield from '@atlaskit/textfield/text-field';
import { Popup } from '@atlaskit/popup/popup';
import type { TriggerProps } from '@atlaskit/popup/types';
import Pagination from '@atlaskit/pagination/pagination';
import MenuGroup from '@atlaskit/menu/menu-group';
import Section from '@atlaskit/menu/section';
import ButtonItem from '@atlaskit/menu/button-item';
import { token } from '@atlaskit/tokens';
import { CmdbObjectCard } from '../cmdb-object-card';
import type { CmdbObject } from '../../rest/cmdb-object-picker';
import { ObjectsDropdown, type DropdownItem } from './objects-dropdown';
import messages from './messages';
import { ErrorMessage } from '@atlaskit/form/error-message';
import { RequiredAsterisk } from '@atlaskit/form/required-asterisk';

/** Number of selected-object cards shown per page in the field. */
const CARDS_PER_PAGE = 10;
const EMPTY_ATTRIBUTE_NAMES: string[] = [];

export interface PaginatedObjectsViewProps {
	/** Currently selected objects (the field value), full objects so cards can render. */
	selectedObjects: CmdbObject[];
	/** Search results to show in the dropdown. */
	searchResults: CmdbObject[];
	/** Whether the field allows multiple values. */
	isMulti: boolean;
	/** Maximum number of objects allowed on the field. */
	limit: number;
	/** Object attributes configured for display in autocomplete results. */
	attributesToDisplayInSearch?: string[];
	/** The custom field's display name, rendered as the field title. */
	fieldLabel?: string;
	/** Current search query. */
	searchQuery: string;
	onSearchChange: (query: string) => void;
	isLoading: boolean;
	hasMore: boolean;
	onLoadMore: () => void;
	/** Called whenever the committed set of objects changes (drives Redux/form state). */
	onSelectionCommit: (objects: CmdbObject[]) => void;
	/** Whether the field is mandatory; renders a required asterisk next to the label (portal enhancements). */
	required?: boolean;
	/** Validation error to display below the field. Validation is owned by the host form (portal enhancements). */
	errorMessage?: string;
}

/**
 * Create-issue style CMDB object field for the portal: shows selected objects as cards with a
 * remove control, an "Add object" trigger that opens a searchable checkbox dropdown with
 * select-all and a confirm footer. Wired through onSelectionCommit so the host form
 * (Redux) stays the single source of truth.
 */
export const PaginatedObjectsView = ({
	selectedObjects,
	searchResults,
	isMulti,
	limit,
	fieldLabel,
	searchQuery,
	onSearchChange,
	isLoading,
	hasMore,
	onLoadMore,
	onSelectionCommit,
	errorMessage,
	attributesToDisplayInSearch = EMPTY_ATTRIBUTE_NAMES,
	required = false,
}: PaginatedObjectsViewProps): JSX.Element => {
	const { formatMessage } = useIntl();
	const addTriggerLabel = messages.addObjectsLabel;
	const hasError = errorMessage != null && errorMessage !== '';
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [isOpen, setIsOpen] = useState(false);
	// Mirror of `isOpen` so event handlers can read the latest value without putting
	// side effects inside a setState updater (which React 18 may invoke more than once).
	const isOpenRef = useRef(isOpen);
	useEffect(() => {
		isOpenRef.current = isOpen;
	}, [isOpen]);
	// In-dropdown checkbox selections (object ids) staged before the user confirms with "Add".
	const [pendingIds, setPendingIds] = useState<string[]>([]);
	// Which page of selected-object cards is shown in the field.
	const [cardsPage, setCardsPage] = useState(0);
	// Search query over the already-added objects (filters the cards list).
	const [listSearchQuery, setListSearchQuery] = useState('');
	// Caches the full CmdbObject for every id that gets checked, so a confirm can still resolve
	// objects that were evicted from the current search results by a later query change.
	const pendingObjectsRef = useRef<Map<string, CmdbObject>>(new Map());

	// The dropdown shows all search results; already-added objects appear pre-checked
	// (their id is seeded into pendingIds on open).
	// Set for O(1) membership checks instead of O(n) Array.includes.
	const pendingIdSet = useMemo(() => new Set(pendingIds), [pendingIds]);
	const attributesToDisplaySet = useMemo(
		() => new Set(attributesToDisplayInSearch),
		[attributesToDisplayInSearch],
	);

	const dropdownItems: DropdownItem[] = useMemo(
		() =>
			searchResults.map((object) => {
				const metadata = object.attributes
					.filter(
						(attribute) =>
							attributesToDisplaySet.has(attribute.objectTypeAttribute.name) ||
							(attributesToDisplaySet.has('Label') && attribute.objectTypeAttribute.label),
					)
					.flatMap((attribute) =>
						attribute.objectAttributeValues
							.map((value) => value.displayValue)
							.filter((value): value is string => Boolean(value)),
					);

				return {
					object,
					isSelected: pendingIdSet.has(object.id),
					metadata: metadata.length > 0 ? metadata : undefined,
				};
			}),
		[searchResults, pendingIdSet, attributesToDisplaySet],
	);

	// Resolve an id to its object from search results or the already-committed set
	// (a pre-checked object may not be on the current search page).
	const objectById = useMemo(() => {
		const map = new Map<string, CmdbObject>();
		selectedObjects.forEach((o) => map.set(o.id, o));
		searchResults.forEach((o) => map.set(o.id, o));
		return map;
	}, [selectedObjects, searchResults]);

	// Ids already committed to the field (the main list).
	const committedIds = useMemo(() => new Set(selectedObjects.map((o) => o.id)), [selectedObjects]);
	// "N selected" = the number of currently-checked rows in the dropdown. On open this is seeded
	// with the main-list objects (so reopen shows them checked), and it drops by one when a row is
	// unchecked.
	const selectedCount = pendingIds.length;
	// The whole field must not exceed the limit. The checked set (pendingIds) already includes the
	// pre-checked committed objects, so the checked count is the eventual field size.
	const isLimitReached = selectedCount >= limit;
	// "Select first N results" / "Showing N" reflect what can still be selected, capped at the limit.
	const allowedLimit = limit;
	// Confirm is enabled when the checked set differs from the current main list (additions/removals).
	const hasChanges = useMemo(() => {
		const pendingSet = new Set(pendingIds);
		const hasAdditions = pendingIds.some((id) => !committedIds.has(id));
		const hasRemovals = selectedObjects.some((o) => !pendingSet.has(o.id));
		return hasAdditions || hasRemovals;
	}, [pendingIds, committedIds, selectedObjects]);

	const closeAndReset = useCallback(() => {
		setIsOpen(false);
		setPendingIds([]);
		pendingObjectsRef.current.clear();
		onSearchChange('');
	}, [onSearchChange]);

	// Fired when the dropdown is dismissed via Cancel (or clicking away).
	const handleCancel = useCallback(() => {
		createAnalyticsEvent({
			analyticsType: UI_EVENT_TYPE,
			action: 'cancelled',
			actionSubject: 'cmdbObjectPicker',
			source: 'cmdbObjectPicker',
			selectedCount: pendingIds.length,
		}).fire();
		closeAndReset();
	}, [createAnalyticsEvent, closeAndReset, pendingIds.length]);

	// Wrap the dropdown search so each query change is tracked (search over the searchable results).
	const handleDropdownSearchChange = useCallback(
		(query: string) => {
			createAnalyticsEvent({
				analyticsType: UI_EVENT_TYPE,
				action: 'searched',
				actionSubject: 'cmdbObjectPicker',
				source: 'cmdbObjectPicker',
				resultCount: searchResults.length,
			}).fire();
			onSearchChange(query);
		},
		[createAnalyticsEvent, onSearchChange, searchResults.length],
	);

	// Wrap load-more (infinite scroll / "Load more") so paginated fetches are tracked.
	const handleLoadMore = useCallback(() => {
		createAnalyticsEvent({
			analyticsType: UI_EVENT_TYPE,
			action: 'loadedMore',
			actionSubject: 'cmdbObjectPicker',
			source: 'cmdbObjectPicker',
			resultCount: searchResults.length,
		}).fire();
		onLoadMore();
	}, [createAnalyticsEvent, onLoadMore, searchResults.length]);

	const handleObjectSelectionChange = useCallback(
		(objectId: string, isSelected: boolean) => {
			if (isSelected) {
				// Cache the full object so confirm can resolve it even if a later search evicts it.
				const object = objectById.get(objectId);
				if (object) {
					pendingObjectsRef.current.set(objectId, object);
				}
			}
			setPendingIds((prev) => {
				if (isSelected) {
					return prev.includes(objectId) ? prev : [...prev, objectId];
				}
				return prev.filter((id) => id !== objectId);
			});
		},
		[objectById],
	);

	const handleSelectAllChange = useCallback(
		(isChecked: boolean) => {
			createAnalyticsEvent({
				limit,
				analyticsType: UI_EVENT_TYPE,
				action: isChecked ? 'selectedAll' : 'deselectedAll',
				actionSubject: 'cmdbObjectSelection',
				source: 'cmdbObjectPicker',
				resultCount: searchResults.length,
			}).fire();
			if (!isChecked) {
				// Only deselect the currently-visible results; preserve pre-checked committed
				// objects that aren't on this search page.
				const visibleIds = new Set(searchResults.map((o) => o.id));
				setPendingIds((prev) => prev.filter((id) => !visibleIds.has(id)));
				return;
			}
			// Fill up to the field limit with the visible results (preserving any already-checked).
			setPendingIds((prev) => {
				const next = [...prev];
				for (const o of searchResults) {
					if (next.length >= limit) {
						break;
					}
					if (!next.includes(o.id)) {
						next.push(o.id);
						pendingObjectsRef.current.set(o.id, o);
					}
				}
				return next.slice(0, limit);
			});
		},
		[searchResults, limit, createAnalyticsEvent],
	);

	const handleConfirm = useCallback(() => {
		// The field becomes exactly the checked set. Unchecked items are dropped. Resolve each id
		// from the current results/committed map, falling back to the pending cache so a checked
		// object isn't lost if a later search evicted it from the results.
		const committed = pendingIds
			.map((id) => objectById.get(id) ?? pendingObjectsRef.current.get(id))
			.filter((o): o is CmdbObject => Boolean(o));
		onSelectionCommit(committed);
		createAnalyticsEvent({
			analyticsType: TRACK_EVENT_TYPE,
			action: 'confirmed',
			actionSubject: 'cmdbObjectSelection',
			selectedCount: committed.length,
		}).fire();
		closeAndReset();
	}, [pendingIds, objectById, onSelectionCommit, closeAndReset, createAnalyticsEvent]);

	const handleRemove = useCallback(
		(objectId: string) => {
			onSelectionCommit(selectedObjects.filter((o) => o.id !== objectId));
			createAnalyticsEvent({
				analyticsType: TRACK_EVENT_TYPE,
				action: 'removed',
				actionSubject: 'cmdbObject',
			}).fire();
		},
		[selectedObjects, onSelectionCommit, createAnalyticsEvent],
	);

	// All visible results are checked (capped by the field limit).
	const isAllSelected =
		searchResults.length > 0 &&
		searchResults.slice(0, allowedLimit).every((o) => pendingIdSet.has(o.id));
	const isIndeterminate = selectedCount > 0 && !isAllSelected;

	// Search over the already-added objects (the cards list), mirroring Create Issue.
	const listFilteredObjects = useMemo(() => {
		const query = listSearchQuery.trim().toLowerCase();
		if (!query) {
			return selectedObjects;
		}
		return selectedObjects.filter((object) => {
			const label = String(object.label ?? '').toLowerCase();
			return label.includes(query);
		});
	}, [selectedObjects, listSearchQuery]);

	// Paginate the filtered selected-object cards shown in the field.
	const totalCardPages = Math.max(Math.ceil(listFilteredObjects.length / CARDS_PER_PAGE), 1);
	// Keep the current page in range when objects are removed/filtered.
	useEffect(() => {
		if (cardsPage > totalCardPages - 1) {
			setCardsPage(totalCardPages - 1);
		}
	}, [cardsPage, totalCardPages]);
	const pagedObjects = useMemo(
		() =>
			listFilteredObjects.slice(
				cardsPage * CARDS_PER_PAGE,
				cardsPage * CARDS_PER_PAGE + CARDS_PER_PAGE,
			),
		[listFilteredObjects, cardsPage],
	);

	const isListSearching = listSearchQuery.trim().length > 0;
	const showListSearch = selectedObjects.length > 1;

	const handleListSearchChange = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			createAnalyticsEvent({
				analyticsType: UI_EVENT_TYPE,
				action: 'searched',
				actionSubject: 'cmdbObjectField',
				source: 'cmdbObjectPicker',
			}).fire();
			setListSearchQuery(event.target.value);
			setCardsPage(0);
		},
		[createAnalyticsEvent],
	);

	const handleClearListSearch = useCallback(() => {
		createAnalyticsEvent({
			analyticsType: UI_EVENT_TYPE,
			action: 'searchCleared',
			actionSubject: 'cmdbObjectField',
			source: 'cmdbObjectPicker',
		}).fire();
		setListSearchQuery('');
		setCardsPage(0);
	}, [createAnalyticsEvent]);

	const fireRemoveEvent = useCallback(
		(mode: 'all' | 'page' | 'searchResults', removedCount: number) => {
			createAnalyticsEvent({
				mode,
				removedCount,
				analyticsType: TRACK_EVENT_TYPE,
				action: 'removed',
				actionSubject: 'cmdbObjectSelection',
			}).fire();
		},
		[createAnalyticsEvent],
	);

	const handleRemoveAll = useCallback(() => {
		fireRemoveEvent('all', selectedObjects.length);
		onSelectionCommit([]);
	}, [onSelectionCommit, selectedObjects.length, fireRemoveEvent]);

	// Remove only the objects shown on the current cards page.
	const handleRemoveThisPage = useCallback(() => {
		const pageIds = new Set(pagedObjects.map((o) => o.id));
		fireRemoveEvent('page', pageIds.size);
		onSelectionCommit(selectedObjects.filter((o) => !pageIds.has(o.id)));
	}, [pagedObjects, selectedObjects, onSelectionCommit, fireRemoveEvent]);

	// Remove all objects matching the current list-search query.
	const handleRemoveSearchResults = useCallback(() => {
		const matchedIds = new Set(listFilteredObjects.map((o) => o.id));
		fireRemoveEvent('searchResults', matchedIds.size);
		onSelectionCommit(selectedObjects.filter((o) => !matchedIds.has(o.id)));
	}, [listFilteredObjects, selectedObjects, onSelectionCommit, fireRemoveEvent]);

	// Open/close state for the create-issue-style "Remove all" options menu.
	const [isRemoveAllOpen, setIsRemoveAllOpen] = useState(false);
	const toggleRemoveAll = useCallback(() => {
		// Compute the next value and fire analytics outside the setState updater
		// (React 18 may invoke updaters more than once, causing duplicate events).
		const willOpen = !isRemoveAllOpen;
		// Only track the open action, not the close.
		if (willOpen) {
			createAnalyticsEvent({
				analyticsType: UI_EVENT_TYPE,
				action: 'opened',
				actionSubject: 'cmdbObjectRemoveAllMenu',
				source: 'cmdbObjectPicker',
				selectedCount: selectedObjects.length,
			}).fire();
		}
		setIsRemoveAllOpen(willOpen);
	}, [createAnalyticsEvent, isRemoveAllOpen, selectedObjects.length]);
	const closeRemoveAll = useCallback(() => setIsRemoveAllOpen(false), []);

	// Track pagination through the selected-object cards, then move to the requested page.
	const handleCardsPageChange = useCallback(
		(_event: React.SyntheticEvent, page: number) => {
			createAnalyticsEvent({
				analyticsType: UI_EVENT_TYPE,
				action: 'paginated',
				actionSubject: 'cmdbObjectField',
				source: 'cmdbObjectPicker',
				pageCount: totalCardPages,
			}).fire();
			setCardsPage(page - 1);
		},
		[createAnalyticsEvent, totalCardPages],
	);

	const handleOpen = useCallback(() => {
		// Keep side effects out of the setState updater (React 18 may invoke updaters
		// more than once). Read the latest open state via the functional ref pattern
		// by toggling based on the current value captured in a ref.
		const willOpen = !isOpenRef.current;
		setIsOpen(willOpen);
		if (willOpen) {
			// Pre-check the already-added objects so the dropdown reflects the current field.
			setPendingIds(selectedObjects.map((o) => o.id));
			// Seed the cache with committed objects so they survive a confirm after searching.
			pendingObjectsRef.current = new Map(selectedObjects.map((o) => [o.id, o]));
			// Fetch the initial (unfiltered) page when the dropdown opens.
			onSearchChange('');
			createAnalyticsEvent({
				analyticsType: UI_EVENT_TYPE,
				action: 'opened',
				actionSubject: 'cmdbObjectPicker',
				selectedCount: selectedObjects.length,
			}).fire();
		}
	}, [onSearchChange, selectedObjects, createAnalyticsEvent]);

	const hasObjects = selectedObjects.length > 0;
	// Show the field trigger when multi-select, or single-select with nothing chosen yet.
	const showTrigger = isMulti || !hasObjects;

	// When objects already exist, the trigger is a compact "+" icon; otherwise the full button.
	const renderAddTrigger = (triggerProps: TriggerProps) =>
		hasObjects ? (
			<Box xcss={addTriggerStyles}>
				<IconButton
					{...triggerProps}
					icon={AddIcon}
					appearance="subtle"
					spacing="compact"
					isDisabled={isLimitReached}
					label={formatMessage(addTriggerLabel)}
					onClick={handleOpen}
				/>
			</Box>
		) : (
			<Button
				{...triggerProps}
				appearance="link"
				spacing="none"
				iconBefore={<AddIcon label="" color={token('color.link', '#0C66E4')} />}
				onClick={handleOpen}
			>
				{formatMessage(addTriggerLabel)}
			</Button>
		);

	const dropdownContent = () => (
		<ObjectsDropdown
			items={dropdownItems}
			displayedCount={searchResults.length}
			searchQuery={searchQuery}
			onSearchChange={handleDropdownSearchChange}
			isLoading={isLoading}
			hasMore={hasMore}
			onLoadMore={handleLoadMore}
			selectedCount={selectedCount}
			allowedLimit={allowedLimit}
			isAllSelected={isAllSelected}
			isIndeterminate={isIndeterminate}
			onSelectAllChange={handleSelectAllChange}
			onObjectSelectionChange={handleObjectSelectionChange}
			isConfirmDisabled={!hasChanges}
			onCancel={handleCancel}
			onConfirm={handleConfirm}
		/>
	);

	const popupTrigger = (
		<Popup
			isOpen={isOpen}
			onClose={closeAndReset}
			placement="top-start"
			content={dropdownContent}
			trigger={renderAddTrigger}
		/>
	);

	// Empty state: title (with required asterisk) on its own line, the
	// "Add objects" trigger on the line below, and an optional error message.
	if (!hasObjects) {
		const fieldTitle = fieldLabel ? (
			<Text size="small" weight="semibold" color="color.text.subtle">
				{fieldLabel}
				{required && <RequiredAsterisk />}
			</Text>
		) : (
			<Box />
		);

		return (
			<Box xcss={rootStyles}>
				<Stack space="space.100" alignInline="start">
					{fieldTitle}
					{showTrigger && popupTrigger}
				</Stack>
				{hasError && <ErrorMessage>{errorMessage}</ErrorMessage>}
			</Box>
		);
	}

	return (
		<Stack space="space.100" xcss={rootStyles}>
			{/* Header: field title on the left, add/remove-all controls on the right. */}
			<Inline alignBlock="center" spread="space-between" space="space.100">
				{fieldLabel ? (
					<Text size="small" weight="semibold" color="color.text.subtle">
						{fieldLabel}
						{required && <RequiredAsterisk />}
					</Text>
				) : (
					<Box />
				)}
				<Inline alignBlock="center" space="space.050">
					<Popup
						isOpen={isRemoveAllOpen}
						onClose={closeRemoveAll}
						placement="bottom-start"
						content={() => (
							<MenuGroup>
								<Section>
									<ButtonItem
										iconAfter={
											<Box xcss={menuItemIconStyles}>
												<CrossIcon label="" color={token('color.icon', '#44546F')} />
											</Box>
										}
										isDisabled={pagedObjects.length === 0}
										onClick={() => {
											handleRemoveThisPage();
											closeRemoveAll();
										}}
									>
										{formatMessage(messages.removeOnThisPage, { count: pagedObjects.length })}
									</ButtonItem>
									{isListSearching && (
										<ButtonItem
											iconAfter={
												<Box xcss={menuItemIconStyles}>
													<CrossIcon label="" color={token('color.icon', '#44546F')} />
												</Box>
											}
											isDisabled={listFilteredObjects.length === 0}
											onClick={() => {
												handleRemoveSearchResults();
												closeRemoveAll();
											}}
										>
											{formatMessage(messages.removeSearchResults, {
												count: listFilteredObjects.length,
											})}
										</ButtonItem>
									)}
									<ButtonItem
										iconAfter={
											<Box xcss={menuItemIconStyles}>
												<CrossIcon label="" color={token('color.icon', '#44546F')} />
											</Box>
										}
										isDisabled={selectedObjects.length === 0}
										onClick={() => {
											handleRemoveAll();
											closeRemoveAll();
										}}
									>
										{formatMessage(messages.removeAddedObjects, {
											count: selectedObjects.length,
										})}
									</ButtonItem>
								</Section>
							</MenuGroup>
						)}
						trigger={(triggerProps) => (
							<Button
								{...triggerProps}
								appearance="subtle"
								spacing="compact"
								iconAfter={<ChevronDownIcon label="" />}
								isDisabled={selectedObjects.length === 0}
								onClick={toggleRemoveAll}
							>
								{formatMessage(messages.removeAll)}
							</Button>
						)}
					/>
					{showTrigger && popupTrigger}
				</Inline>
			</Inline>

			{showListSearch && (
				<Box xcss={listSearchStyles}>
					<Textfield
						value={listSearchQuery}
						onChange={handleListSearchChange}
						placeholder={formatMessage(messages.searchPlaceholder)}
						aria-label={formatMessage(messages.search)}
						elemBeforeInput={
							<Box xcss={listSearchIconStyles}>
								<SearchIcon label="" size="small" />
							</Box>
						}
					/>
				</Box>
			)}
			{isListSearching && listFilteredObjects.length === 0 && (
				<Box xcss={listSearchEmptyStyles}>
					<Text color="color.text.subtle">{formatMessage(messages.noResults)}</Text>{' '}
					<Button appearance="link" spacing="none" onClick={handleClearListSearch}>
						{formatMessage(messages.clearSearch)}
					</Button>
				</Box>
			)}
			<Stack space="space.050">
				{pagedObjects.map((object) => (
					<Box key={object.id} xcss={cardRowStyles}>
						<CmdbObjectCard cmdbObject={object} hideExpand />
						<Box xcss={removeButtonStyles}>
							<IconButton
								icon={SelectClearIcon}
								appearance="subtle"
								spacing="compact"
								shape="circle"
								label={formatMessage(messages.removeObject, { objectLabel: object.label })}
								onClick={() => handleRemove(object.id)}
							/>
						</Box>
					</Box>
				))}
				{listFilteredObjects.length > 0 && (
					<Box xcss={paginationStyles}>
						<Box xcss={paginationRangeStyles}>
							<Text size="medium" color="color.text.subtlest">
								{formatMessage(messages.paginationRangeInline, {
									start: cardsPage * CARDS_PER_PAGE + 1,
									end: Math.min((cardsPage + 1) * CARDS_PER_PAGE, listFilteredObjects.length),
									total: listFilteredObjects.length,
								})}
							</Text>
						</Box>
						{totalCardPages > 1 && (
							<Box xcss={paginationControlsStyles}>
								<Pagination
									label={formatMessage(messages.pageLabel)}
									pageLabel={formatMessage(messages.pageLabel)}
									nextLabel={formatMessage(messages.nextPage)}
									previousLabel={formatMessage(messages.previousPage)}
									pages={Array.from({ length: totalCardPages }, (_, index) => index + 1)}
									selectedIndex={cardsPage}
									onChange={handleCardsPageChange}
									max={7}
								/>
							</Box>
						)}
					</Box>
				)}
			</Stack>
			{hasError && <ErrorMessage>{errorMessage}</ErrorMessage>}
		</Stack>
	);
};

const rootStyles = xcss({
	paddingBlockStart: 'space.200',
});

const paginationStyles = xcss({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	columnGap: 'space.150',
	flexWrap: 'wrap',
	paddingBlockStart: 'space.050',
});

const paginationRangeStyles = xcss({
	flexShrink: 0,
});

const paginationControlsStyles = xcss({
	display: 'flex',
	alignItems: 'center',
});

const cardRowStyles = xcss({
	position: 'relative',
	width: '100%',
});

// Search bar above the added-objects cards list.
const listSearchStyles = xcss({
	paddingBlockEnd: 'space.050',
});

const listSearchIconStyles = xcss({
	display: 'flex',
	alignItems: 'center',
	paddingInlineStart: 'space.075',
	color: 'color.text.subtle',
});

// "No results matching your search." + Clear search empty state (centered).
const listSearchEmptyStyles = xcss({
	paddingBlock: 'space.100',
	textAlign: 'center',
});

// Trailing cross icon inside each Remove-all menu option (matches Create Issue).
const menuItemIconStyles = xcss({
	display: 'flex',
	alignItems: 'center',
});

// Overlay the remove control in the card's top-right corner without extra wrapper chrome.
const removeButtonStyles = xcss({
	position: 'absolute',
	top: 'space.100',
	right: 'space.100',
	zIndex: 'card',
});

// "+" add trigger: bordered at rest, background change on hover (matches Create Issue).
const addTriggerStyles = xcss({
	borderRadius: 'radius.small',
	borderWidth: 'border.width',
	borderStyle: 'solid',
	borderColor: 'color.border',
	':hover': {
		backgroundColor: 'color.background.neutral.subtle.hovered',
	},
});
